import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { useScrollRestoration } from '@redpanda/utils';
import CustomerManagePage from './pages/CustomerManagePage';
import CustomerDetailPage from './pages/CustomerDetailPage';

import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import DataIOPage from './pages/DataIOPage';
import ProjectListPage from './pages/ProjectListPage';
import QuoteListPage from './pages/QuoteListPage';
import PaymentListPage from './pages/PaymentListPage';
import TaskListPage from './pages/TaskListPage';
import TagListPage from './pages/TagListPage';
import OpportunityListPage from './pages/OpportunityListPage';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import OpportunityKanbanPage from './pages/OpportunityKanbanPage';
import OpportunityStageManagePage from './pages/OpportunityStageManagePage';
import ReportPage from './pages/ReportPage';
import ActivityPage from './pages/ActivityPage';
import ManualPage from './pages/ManualPage';
import ProfilePage from './pages/ProfilePage';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage, MobileTopBar } from '@redpanda/ui';
import PrivateRoute from './components/PrivateRoute';
import Sidebar from './components/Sidebar';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { Role } from '@redpanda/types';

/** 捲動管理（共用 useScrollRestoration）：重新整理、上一頁、麵包屑回列表時還原捲動位置，進新頁面捲回頂端 */
const ScrollManager: React.FC = () => {
  useScrollRestoration(useLocation(), useNavigationType());
  return null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Theme application removed - handled by UserContext to prevent duplicate renders

  // 設定動態頁面標題
  useEffect(() => {
    document.title = `RedPanda ${t('app.title')}`;
  }, [t]);

  // 記錄當前頁面（換頁捲動交給 ScrollManager）
  useEffect(() => {
    // 記錄當前活動的系統頁面，供使用手冊自動導航使用
    const systemPaths = ['/', '/customers', '/quotes', '/projects', '/payments', '/tasks', '/tags', '/opportunities', '/reports'];
    const currentPath = location.pathname;

    // 檢查是否在系統頁面或其子頁面
    let activeSystemPath = systemPaths.find(path => {
      if (path === '/') {
        return currentPath === '/';
      }
      return currentPath.startsWith(path);
    });

    if (activeSystemPath) {
      sessionStorage.setItem('lastActivePage', activeSystemPath);
    }
  }, [location.pathname]);

  

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          {/* 品牌 LOGO - 放大到兩倍 */}
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-2xl font-semibold text-gray-900 dark:text-white mb-6">RedPanda CRM</div>

          {/* 載入動畫 */}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`App bg-white dark:bg-slate-950 min-h-screen flex transition-colors duration-300`}>
      <Toaster position="bottom-right" />
      <ScrollManager />

      {/* 左側導覽列 */}
      {isAuthenticated && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 主要內容區域 */}
      <div className={`flex-1 min-w-0 flex flex-col ${isAuthenticated ? 'md:ml-48' : ''}`}>
        {/* 手機版頂部列（R27）：取代原本浮動在左上角、會蓋住頁標題的選單按鈕 */}
        {isAuthenticated && (
          <MobileTopBar
            title={`RedPanda ${t('app.title')}`}
            onOpenMenu={() => setIsMobileSidebarOpen(true)}
            menuLabel={t('common.navigation.openMenu')}
          />
        )}

        {/* 主要內容 */}
        <main className="flex-grow">
        <Routes>
          {/* Authentication routes removed - using identity-service OAuth */}
          <Route path="/manual" element={<ManualPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/faq" element={<FAQPage categories={['general', 'account', 'billing']} />} />
          {/* Old SSO routes removed - using Google-style OAuth now */}
          
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/customers" element={<CustomerManagePage />} />
            <Route path="/customers/archived" element={<CustomerManagePage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/projects" element={<ProjectListPage />} />
            <Route path="/quotes" element={<QuoteListPage />} />
            <Route path="/payments" element={<PaymentListPage />} />
            <Route path="/tasks" element={<TaskListPage />} />
            <Route path="/tags" element={<TagListPage />} />
            <Route path="/opportunities" element={<OpportunityListPage />} />
            <Route path="/opportunities/kanban" element={<OpportunityKanbanPage />} />
            <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
            <Route path="/reports" element={<ReportPage />} />
            <Route path="/activities" element={<ActivityPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN]} />}>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/data-io" element={<DataIOPage />} />
          </Route>
          
          <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN, Role.ADMIN]} />}>
            <Route path="/opportunities/stages" element={<OpportunityStageManagePage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>

        {/* 底部 */}
      </div>
    </div>
  );
}


const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ColorThemeProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;